/**
 * The reference's toast, as a React context — `toast()` in `m01-utils.js`.
 *
 * Same markup (`.toast-host` > `.toast` with the leading `<i>` square), same dwell (2800ms), same
 * `aria-live="polite"` host, so a message is announced without stealing focus. The host is
 * mounted once by the demo shell; `useToast()` is what a screen calls.
 */
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ToastKind = '' | 'ok' | 'warn' | 'danger';

/** The reference's dwell, unchanged. */
const DWELL_MS = 2800;

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  /**
   * Absolute deadline rather than a duration, so re-scheduling the removal — which happens on
   * every change to the list, and on React's extra StrictMode mount pass — can neither shorten
   * nor lengthen the 2800ms a message is on screen.
   */
  expiresAt: number;
}

type ToastFn = (message: string, kind?: ToastKind) => void;

const ToastContext = createContext<ToastFn | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const push = useCallback<ToastFn>((message, kind = '') => {
    const id = next.current;
    next.current += 1;
    setItems((list) => [...list, { id, message, kind, expiresAt: Date.now() + DWELL_MS }]);
  }, []);

  /*
   * The removal timers belong to this provider, so they live and die with it.
   *
   * Scheduling them inside `push` leaked one timer per toast: it outlived unmount and fired into
   * a tree that was no longer there — in a test runner, after the environment itself had been
   * torn down, which is the `window is not defined` that failed `frontend-ci` #273 while all 910
   * tests passed. Owning them in an effect means the cleanup clears whatever is still pending,
   * whether that cleanup comes from a real unmount or from StrictMode's extra pass, and the
   * absolute deadline above keeps the visible dwell exactly what it was.
   */
  useEffect(() => {
    if (items.length === 0) return;
    const now = Date.now();
    const timers = items.map((item) =>
      window.setTimeout(
        () => setItems((list) => list.filter((t) => t.id !== item.id)),
        Math.max(0, item.expiresAt - now),
      ),
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [items]);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-host" id="toastHost" aria-live="polite">
        {items.map((t) => (
          <div className={`toast ${t.kind}`} key={t.id}>
            <i />
            {t.message}
          </div>
        ))}
      </div>
      <div id="modalHost" />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastFn {
  const fn = useContext(ToastContext);
  if (fn === null) throw new Error('useToast must be used inside ToastProvider');
  return fn;
}
